import React, { useState, useEffect, useRef } from 'react';
import ConfirmModal from '@/components/ConfirmModal';
import Toast from '@/components/Toast';
import LoadingOverlay from '@/components/LoadingOverlay';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import { dictionaryAdminApi } from '@/features/admin/dictionaryAdminApi';
import { Article } from '@/types/article';
import { Category } from '@/types/category';
import { WordDictionary } from '@/types/wordDictionary';
import {
  useAdminCategoriesByCountryQuery,
  useAdminTopicsAsCategoriesQuery,
} from '@hooks/useConfigureQueries';

type ArticleLevel = 'A' | 'B' | 'C';

/** Retry an async operation up to `maxAttempts` times with exponential back-off. */
async function retryAsync<T>(fn: () => Promise<T>, maxAttempts = 3, baseDelayMs = 500): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, baseDelayMs * Math.pow(2, attempt - 1)));
      }
    }
  }
  throw lastError;
}

const emptyDictionaryByLevel: Record<ArticleLevel, WordDictionary[]> = {
  A: [],
  B: [],
  C: [],
};

const defaultDictionaryPanelOpenByLevel: Record<ArticleLevel, boolean> = {
  A: false,
  B: false,
  C: false,
};

export const PublishArticle: React.FC = () => {
  const { country, id: articleId } = useParams<{ country?: string; id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [article, setArticle] = useState<Article | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const [showPublishConfirm, setShowPublishConfirmation] = useState(false);
  const notifTimeout = useRef<number | null>(null);
  const [originalStatus, setOriginalStatus] = useState<number | null>(null);
  const [publishedAtError, setPublishedAtError] = useState<string | null>(null);
  const [dictionaryByLevel, setDictionaryByLevel] =
    useState<Record<ArticleLevel, WordDictionary[]>>(emptyDictionaryByLevel);
  const [dictionaryPanelOpenByLevel, setDictionaryPanelOpenByLevel] = useState<
    Record<ArticleLevel, boolean>
  >(defaultDictionaryPanelOpenByLevel);
  const [generatingDictionaryWords, setGeneratingDictionaryWords] = useState<Set<string>>(
    new Set()
  );
  const [splittingPhraseWords, setSplittingPhraseWords] = useState<Set<string>>(new Set());
  const [splitPhraseEditValues, setSplitPhraseEditValues] = useState<Record<string, string>>({});
  const [editingEntryValues, setEditingEntryValues] = useState<Record<string, string>>({});
  const [editingEntries, setEditingEntries] = useState<Set<string>>(new Set());
  const [savingEntries, setSavingEntries] = useState<Set<string>>(new Set());
  const [deletingEntries, setDeletingEntries] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<{
    level: ArticleLevel;
    word: string;
  } | null>(null);
  const { data: adminCountryCategories = [] } = useAdminCategoriesByCountryQuery(
    country,
    Boolean(country)
  );
  const { data: adminTopicCategories = [] } = useAdminTopicsAsCategoriesQuery(!country);
  const categories: Category[] = country ? adminCountryCategories : adminTopicCategories;

  const getLevelLabel = (level: 'A' | 'B' | 'C'): string => {
    if (level === 'A') return 'Easy';
    if (level === 'B') return 'Medium';
    return 'Hard';
  };

  /**
   * Check if article is published (only for publish tab)
   * @param article
   * @returns -1 if not published, 0 if published but publishedAt date > now, 1 if published and publishedAt date <= now
   */
  const isPublished = (article: Article | null) => {
    if (!article) return -1;

    if (!article.publishedAt) return -1;
    const publishedDate = new Date(article.publishedAt);
    const now = new Date();
    if (publishedDate > now) return 0;
    return 1;
  };

  const toLocalDatetimeInput = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    const YYYY = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const DD = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${YYYY}-${MM}-${DD}T${hh}:${mm}`;
  };

  const nowLocalDatetimeInput = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const YYYY = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const DD = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${YYYY}-${MM}-${DD}T${hh}:${mm}`;
  };

  const fromLocalDatetimeInput = (val: string) => {
    if (!val) return '';
    // treat as local time and convert to ISO
    const d = new Date(val);
    return d.toISOString();
  };

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 3000
  ) => {
    setNotification({ message, type });
    if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    notifTimeout.current = window.setTimeout(() => setNotification(null), duration);
  };

  useEffect(() => {
    return () => {
      if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    };
  }, []);

  const fetchArticleDetail = async (country: string, articleId: string) => {
    setIsLoading(true);
    try {
      const data = await articleAdminApi.getById(country, articleId, {
        includeVocabulary: true,
      });
      // preserve original status based on fetched data so editing publishedAt won't change it
      setOriginalStatus(isPublished((data || {}) as Article));
      setArticle(data);
    } catch (error) {
      showNotification('Failed to fetch article details', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (articleId) {
      fetchArticleDetail(country || '', articleId);
    }
  }, [country, articleId]);

  const normalizeWords = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value.map(word => String(word || '').trim()).filter(word => word.length > 0);
  };

  const getExtractWordsByLevel = (articleValue: Article | null, level: ArticleLevel): string[] => {
    if (!articleValue) return [];

    const articleData = articleValue as unknown as Record<string, unknown>;
    const usWords = normalizeWords(articleData[`extractWords${level}LevelUS`]);
    if (usWords.length > 0) return usWords;

    return normalizeWords(articleData[`extractWords${level}Level`]);
  };

  const isMultiWordPhrase = (word: string): boolean =>
    String(word || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean).length >= 2;

  const parseSplitWordsInput = (value: string): string[] => {
    const result: string[] = [];
    const seen = new Set<string>();

    for (const raw of value.split(',')) {
      const word = raw.trim().toLowerCase();
      if (!word) continue;
      if (seen.has(word)) continue;
      seen.add(word);
      result.push(word);
    }

    return result;
  };

  const toFallbackDictionaryEntries = (words: string[]): WordDictionary[] =>
    words.map(word => ({
      word: String(word || '')
        .trim()
        .toLowerCase(),
      entries: [],
    }));

  const buildDictionaryStateFromArticle = (
    articleValue: Article | null
  ): Record<ArticleLevel, WordDictionary[]> => {
    if (!articleValue) {
      return emptyDictionaryByLevel;
    }

    const levelAWords = getExtractWordsByLevel(articleValue, 'A');
    const levelBWords = getExtractWordsByLevel(articleValue, 'B');
    const levelCWords = getExtractWordsByLevel(articleValue, 'C');

    return {
      A:
        Array.isArray(articleValue.vocabularyALevel) && articleValue.vocabularyALevel.length > 0
          ? articleValue.vocabularyALevel
          : toFallbackDictionaryEntries(levelAWords),
      B:
        Array.isArray(articleValue.vocabularyBLevel) && articleValue.vocabularyBLevel.length > 0
          ? articleValue.vocabularyBLevel
          : toFallbackDictionaryEntries(levelBWords),
      C:
        Array.isArray(articleValue.vocabularyCLevel) && articleValue.vocabularyCLevel.length > 0
          ? articleValue.vocabularyCLevel
          : toFallbackDictionaryEntries(levelCWords),
    };
  };

  useEffect(() => {
    if (!article) {
      setDictionaryByLevel(emptyDictionaryByLevel);
      return;
    }

    setDictionaryByLevel(buildDictionaryStateFromArticle(article));
  }, [article]);

  const renderDictionaryByLevel = (level: ArticleLevel) => {
    const entries = dictionaryByLevel[level];
    const isOpen = dictionaryPanelOpenByLevel[level];

    if (!entries || entries.length === 0) {
      return null;
    }

    const entriesWithoutDictionary = entries.filter(
      entry => !Array.isArray(entry.entries) || entry.entries.length === 0
    ).length;

    return (
      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-left"
          onClick={() =>
            setDictionaryPanelOpenByLevel(prev => ({
              ...prev,
              [level]: !prev[level],
            }))
          }
        >
          <span className="text-sm font-medium text-slate-700">
            Dictionary ({entries.length} words
            {`, ${entriesWithoutDictionary} without definition`})
          </span>
          <span className="text-xs font-medium text-slate-600">{isOpen ? 'Hide' : 'Show'}</span>
        </button>

        {isOpen && (
          <div className="space-y-2 border-t border-slate-200 px-3 py-3">
            {entries.map(entry => {
              const dictionaryEntries = Array.isArray(entry.entries) ? entry.entries : [];
              const previewEntries = dictionaryEntries.slice(0, 2);
              const normalizedWord = String(entry.word || '')
                .trim()
                .toLowerCase();
              const isGenerating = generatingDictionaryWords.has(normalizedWord);
              const isSplitting = splittingPhraseWords.has(normalizedWord);
              const splitEditValue = splitPhraseEditValues[normalizedWord];
              const isSplitMode = typeof splitEditValue === 'string';

              return (
                <div key={entry.word} className="rounded border border-slate-200 bg-white p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {isSplitMode ? (
                        <input
                          type="text"
                          value={splitEditValue}
                          onChange={e =>
                            setSplitPhraseEditValues(prev => ({
                              ...prev,
                              [normalizedWord]: e.target.value,
                            }))
                          }
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleSaveSplitPhrase(level, entry.word);
                            }
                          }}
                          placeholder="word1, word2, word3"
                          className="w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-800"
                        />
                      ) : editingEntries.has(normalizedWord) ? (
                        <input
                          type="text"
                          value={editingEntryValues[normalizedWord] || entry.word}
                          onChange={e =>
                            setEditingEntryValues(prev => ({
                              ...prev,
                              [normalizedWord]: e.target.value,
                            }))
                          }
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleSaveEditedEntry(level, entry.word);
                            }
                          }}
                          className="w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-800"
                        />
                      ) : (
                        <p className="text-sm font-semibold text-slate-800">{entry.word}</p>
                      )}
                    </div>

                    <div className="ml-3 flex items-center gap-2">
                      {editingEntries.has(normalizedWord) ? (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-7 px-2 py-1 text-xs"
                            disabled={savingEntries.has(normalizedWord)}
                            onClick={() => void handleSaveEditedEntry(level, entry.word)}
                          >
                            {savingEntries.has(normalizedWord) ? 'Saving...' : 'Save'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-7 px-2 py-1 text-xs"
                            onClick={() => handleCancelEditEntry(normalizedWord)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-7 px-2 py-1 text-xs"
                            onClick={() => handleStartEditEntry(normalizedWord, entry.word)}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-7 px-2 py-1 text-xs text-red-600"
                            disabled={deletingEntries.has(normalizedWord)}
                            onClick={() =>
                              setDeleteConfirm({ level, word: String(entry.word || '') })
                            }
                          >
                            {deletingEntries.has(normalizedWord) ? 'Deleting...' : 'Delete'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {previewEntries.length > 0 ? (
                    <div className="mt-1 space-y-1">
                      {previewEntries.map((dictionaryEntry, dictionaryEntryIndex) => (
                        <React.Fragment key={`${entry.word}-${dictionaryEntryIndex}`}>
                          <p className="text-xs leading-relaxed text-slate-700">
                            {(dictionaryEntry.ipaUS || dictionaryEntry.ipaUK) && (
                              <span className="mr-2 font-mono text-slate-600">
                                {dictionaryEntry.ipaUS || dictionaryEntry.ipaUK}
                              </span>
                            )}
                            {dictionaryEntry.partOfSpeech
                              ? `[${dictionaryEntry.partOfSpeech}] `
                              : ''}
                            {dictionaryEntry.meaningVN || 'No Vietnamese meaning'}
                          </p>
                          <p>
                            {dictionaryEntry.exampleEN && (
                              <span className="ml-2 text-xs text-slate-500">
                                Example: {dictionaryEntry.exampleEN}
                              </span>
                            )}
                            {dictionaryEntry.exampleVN && (
                              <span className="ml-2 text-xs text-slate-500">
                                Example VN: {dictionaryEntry.exampleVN}
                              </span>
                            )}
                          </p>
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 flex w-full items-center justify-between gap-3">
                      <p className="text-xs text-slate-500">No dictionary entry yet</p>
                      <div className="ml-auto flex items-center gap-2">
                        {!isSplitMode && isMultiWordPhrase(entry.word) && (
                          <Button
                            type="button"
                            variant="outline"
                            className="h-7 px-2 py-1 text-xs"
                            disabled={isSplitting || isGenerating}
                            onClick={() =>
                              setSplitPhraseEditValues(prev => ({
                                ...prev,
                                [normalizedWord]: entry.word.split(/\s+/).join(', '),
                              }))
                            }
                          >
                            Split phrase
                          </Button>
                        )}

                        {isSplitMode ? (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              className="h-7 px-2 py-1 text-xs"
                              disabled={isSplitting}
                              onClick={() => void handleSaveSplitPhrase(level, entry.word)}
                            >
                              {isSplitting ? 'Saving...' : 'Save split'}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="h-7 px-2 py-1 text-xs"
                              disabled={isSplitting}
                              onClick={() =>
                                setSplitPhraseEditValues(prev => {
                                  const next = { ...prev };
                                  delete next[normalizedWord];
                                  return next;
                                })
                              }
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            className="h-7 px-2 py-1 text-xs"
                            disabled={isGenerating || isSplitting}
                            onClick={() => handleGenerateDictionaryForWord(level, entry.word)}
                          >
                            {isGenerating ? 'Generating...' : 'Generate Dictionary'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleGenerateDictionaryForWord = async (level: ArticleLevel, word: string) => {
    const normalizedWord = String(word || '')
      .trim()
      .toLowerCase();
    if (!normalizedWord) return;

    setGeneratingDictionaryWords(prev => {
      const next = new Set(prev);
      next.add(normalizedWord);
      return next;
    });

    try {
      const response = await dictionaryAdminApi.searchAndGenerate(normalizedWord);
      const payload = response?.data || response;
      const generatedEntry = (payload?.entry || payload) as WordDictionary;

      if (!generatedEntry?.word) {
        throw new Error('Dictionary generation did not return an entry');
      }

      const nextWord = generatedEntry.word;
      const nextWordNormalized = nextWord.trim().toLowerCase();

      setDictionaryByLevel(prev => ({
        ...prev,
        [level]: prev[level].map(item =>
          item.word.trim().toLowerCase() === nextWordNormalized ? generatedEntry : item
        ),
      }));

      showNotification(`Dictionary generated for "${nextWord}"`, 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : 'Failed to generate dictionary entry',
        'error'
      );
    } finally {
      setGeneratingDictionaryWords(prev => {
        const next = new Set(prev);
        next.delete(normalizedWord);
        return next;
      });
    }
  };

  const handleSaveSplitPhrase = async (level: ArticleLevel, originalPhrase: string) => {
    const normalizedPhrase = String(originalPhrase || '')
      .trim()
      .toLowerCase();
    if (!normalizedPhrase) return;

    const splitValue = splitPhraseEditValues[normalizedPhrase] || '';
    const splitWords = parseSplitWordsInput(splitValue);

    if (splitWords.length === 0) {
      showNotification('Please enter split words', 'error');
      return;
    }

    const isVi = !country || country.toUpperCase() === 'VI';
    const fieldName = isVi ? `extractWords${level}LevelUS` : `extractWords${level}Level`;
    const vocabFieldName = `vocabulary${level}Level`;
    const currentWordsFromArticle = normalizeWords(
      (article as unknown as Record<string, unknown> | null)?.[fieldName]
    );
    const currentVocabFromArticle =
      (Array.isArray(dictionaryByLevel[level]) && dictionaryByLevel[level].length > 0
        ? dictionaryByLevel[level]
        : ((article as unknown as Record<string, unknown> | null)?.[vocabFieldName] as
            | WordDictionary[]
            | undefined)) || [];
    const currentExtractWords = normalizeWords(
      (article as unknown as Record<string, unknown> | null)?.[fieldName]
    ).map(word => word.toLowerCase());
    const currentExtractWordsSet = new Set(currentExtractWords);
    const allSplitWordsAlreadyInExtracted = splitWords.every(splitWord =>
      currentExtractWordsSet.has(splitWord)
    );

    setSplittingPhraseWords(prev => {
      const next = new Set(prev);
      next.add(normalizedPhrase);
      return next;
    });

    try {
      if (allSplitWordsAlreadyInExtracted) {
        const nextWords = currentWordsFromArticle.filter(
          word => word.trim().toLowerCase() !== normalizedPhrase
        );
        const nextVocab = currentVocabFromArticle.filter(
          entry => entry.word.trim().toLowerCase() !== normalizedPhrase
        );

        if (!articleId) {
          throw new Error('Missing article id');
        }

        await retryAsync(() =>
          articleAdminApi.update(country || '', articleId, {
            [fieldName]: nextWords,
            [vocabFieldName]: nextVocab,
          })
        );

        await dictionaryAdminApi.deleteFixedPhrase(originalPhrase);

        setDictionaryByLevel(prev => ({
          ...prev,
          [level]: (prev[level] || []).filter(
            entry => entry.word.trim().toLowerCase() !== normalizedPhrase
          ),
        }));

        setArticle(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            [fieldName]: nextWords,
            [vocabFieldName]: nextVocab,
          };
        });

        setSplitPhraseEditValues(prev => {
          const next = { ...prev };
          delete next[normalizedPhrase];
          return next;
        });

        showNotification('Phrase removed from extracted words and fixed phrases', 'success');
        return;
      }

      const results = await Promise.allSettled(
        splitWords.map(splitWord => dictionaryAdminApi.searchAndGenerate(splitWord))
      );

      const generatedMap = new Map<string, WordDictionary>();
      let successCount = 0;

      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const payload = result.value?.data || result.value;
        const generatedEntry = (payload?.entry || payload) as WordDictionary;
        if (!generatedEntry?.word) continue;
        generatedMap.set(generatedEntry.word.trim().toLowerCase(), generatedEntry);
        successCount += 1;
      }

      if (successCount === 0) {
        throw new Error('Failed to generate dictionary for split words');
      }

      const replacementEntries = splitWords.map(splitWord => {
        return (
          generatedMap.get(splitWord) || {
            word: splitWord,
            entries: [],
          }
        );
      });

      setDictionaryByLevel(prev => {
        const currentLevelEntries = prev[level] || [];
        const targetIndex = currentLevelEntries.findIndex(
          item => item.word.trim().toLowerCase() === normalizedPhrase
        );

        if (targetIndex < 0) return prev;

        const nextLevelEntriesRaw = [
          ...currentLevelEntries.slice(0, targetIndex),
          ...replacementEntries,
          ...currentLevelEntries.slice(targetIndex + 1),
        ];

        // De-duplicate by normalized word while preserving the first occurrence order.
        const seenWords = new Set<string>();
        const nextLevelEntries = nextLevelEntriesRaw.filter(entry => {
          const key = String(entry.word || '')
            .trim()
            .toLowerCase();
          if (!key || seenWords.has(key)) return false;
          seenWords.add(key);
          return true;
        });

        return {
          ...prev,
          [level]: nextLevelEntries,
        };
      });

      const nextWordsBase = currentWordsFromArticle.filter(
        word => word.trim().toLowerCase() !== normalizedPhrase
      );
      const existingWordSet = new Set(nextWordsBase.map(word => word.trim().toLowerCase()));
      const wordsToAppend = splitWords.filter(splitWord => !existingWordSet.has(splitWord));
      const nextWords = nextWordsBase.concat(wordsToAppend);

      const nextVocabRaw = [
        ...currentVocabFromArticle.filter(
          entry => entry.word.trim().toLowerCase() !== normalizedPhrase
        ),
        ...replacementEntries,
      ];
      const seenVocabWords = new Set<string>();
      const nextVocab = nextVocabRaw.filter(entry => {
        const key = String(entry.word || '')
          .trim()
          .toLowerCase();
        if (!key || seenVocabWords.has(key)) return false;
        seenVocabWords.add(key);
        return true;
      });

      if (!articleId) {
        throw new Error('Missing article id');
      }

      await retryAsync(() =>
        articleAdminApi.update(country || '', articleId, {
          [fieldName]: nextWords,
          [vocabFieldName]: nextVocab,
        })
      );

      await dictionaryAdminApi.deleteFixedPhrase(originalPhrase);

      // Update the article's extractWords and vocabulary to replace the phrase with split words
      setArticle(prev => {
        if (!prev) return prev;

        return {
          ...prev,
          [fieldName]: nextWords,
          [vocabFieldName]: nextVocab,
        };
      });

      setSplitPhraseEditValues(prev => {
        const next = { ...prev };
        delete next[normalizedPhrase];
        return next;
      });

      if (successCount < splitWords.length) {
        showNotification(
          `Phrase split saved. Generated ${successCount}/${splitWords.length} dictionary entries`,
          'info'
        );
      } else {
        showNotification(
          `Phrase split saved and generated ${splitWords.length} dictionary entries`,
          'success'
        );
      }
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : 'Failed to split phrase and generate dictionary',
        'error'
      );
    } finally {
      setSplittingPhraseWords(prev => {
        const next = new Set(prev);
        next.delete(normalizedPhrase);
        return next;
      });
    }
  };

  const handleStartEditEntry = (normalizedWord: string, currentWord: string) => {
    setEditingEntryValues(prev => ({ ...prev, [normalizedWord]: currentWord }));
    setEditingEntries(prev => {
      const next = new Set(prev);
      next.add(normalizedWord);
      return next;
    });
  };

  const handleCancelEditEntry = (normalizedWord: string) => {
    setEditingEntryValues(prev => {
      const next = { ...prev };
      delete next[normalizedWord];
      return next;
    });
    setEditingEntries(prev => {
      const next = new Set(prev);
      next.delete(normalizedWord);
      return next;
    });
  };

  const handleSaveEditedEntry = async (level: ArticleLevel, originalWord: string) => {
    const normalizedOriginal = String(originalWord || '')
      .trim()
      .toLowerCase();
    const newRaw = editingEntryValues[normalizedOriginal] || '';
    const newWord = newRaw.trim().toLowerCase();
    if (!newWord) {
      showNotification('Please enter a valid word', 'error');
      return;
    }

    const vocabFieldName = `vocabulary${level}Level`;

    setSavingEntries(prev => {
      const next = new Set(prev);
      next.add(normalizedOriginal);
      return next;
    });

    try {
      if (!articleId) throw new Error('Missing article id');

      const currentVocabFromArticle =
        (Array.isArray(dictionaryByLevel[level]) && dictionaryByLevel[level].length > 0
          ? dictionaryByLevel[level]
          : ((article as unknown as Record<string, unknown> | null)?.[vocabFieldName] as
              | WordDictionary[]
              | undefined)) || [];

      // Replace the matching entry's word
      const updatedRaw = currentVocabFromArticle.map(entry => {
        const key = String(entry.word || '')
          .trim()
          .toLowerCase();
        return key === normalizedOriginal ? { ...entry, word: newWord } : entry;
      });

      // De-duplicate by normalized word preserving order
      const seen = new Set<string>();
      const updated = updatedRaw.filter(e => {
        const k = String(e.word || '')
          .trim()
          .toLowerCase();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      // Also update extractWords field so server-side vocabulary recomputation matches UI
      const isVi = !country || country.toUpperCase() === 'VI';
      const fieldName = isVi ? `extractWords${level}LevelUS` : `extractWords${level}Level`;

      const currentWordsFromArticle = normalizeWords(
        (article as unknown as Record<string, unknown> | null)?.[fieldName]
      ).map(w => w.toLowerCase());

      const nextWordsRaw = currentWordsFromArticle.map(w =>
        w === normalizedOriginal ? newWord : w
      );
      // If original word was not present, append the new word
      if (
        !currentWordsFromArticle.includes(normalizedOriginal) &&
        !nextWordsRaw.includes(newWord)
      ) {
        nextWordsRaw.push(newWord);
      }

      await retryAsync(() =>
        articleAdminApi.update(country || '', articleId, {
          [vocabFieldName]: updated,
          [fieldName]: nextWordsRaw,
        })
      );

      // Update local state
      setDictionaryByLevel(prev => ({
        ...prev,
        [level]: (prev[level] || []).map(item =>
          String(item.word || '')
            .trim()
            .toLowerCase() === normalizedOriginal
            ? { ...item, word: newWord }
            : item
        ),
      }));

      setArticle(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          [vocabFieldName]: updated,
          [fieldName]: nextWordsRaw,
        } as Article;
      });

      handleCancelEditEntry(normalizedOriginal);
      showNotification('Dictionary entry updated', 'success');
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to save entry', 'error');
    } finally {
      setSavingEntries(prev => {
        const next = new Set(prev);
        next.delete(normalizedOriginal);
        return next;
      });
    }
  };

  const handleDeleteEntry = async (level: ArticleLevel, wordToDelete: string) => {
    const normalizedWord = String(wordToDelete || '')
      .trim()
      .toLowerCase();
    if (!normalizedWord) return;

    const vocabFieldName = `vocabulary${level}Level`;

    setDeletingEntries(prev => {
      const next = new Set(prev);
      next.add(normalizedWord);
      return next;
    });

    try {
      if (!articleId) throw new Error('Missing article id');

      const currentVocabFromArticle =
        (Array.isArray(dictionaryByLevel[level]) && dictionaryByLevel[level].length > 0
          ? dictionaryByLevel[level]
          : ((article as unknown as Record<string, unknown> | null)?.[vocabFieldName] as
              | WordDictionary[]
              | undefined)) || [];

      const nextVocab = currentVocabFromArticle.filter(
        e =>
          String(e.word || '')
            .trim()
            .toLowerCase() !== normalizedWord
      );

      // Also remove from extractWords so server-side recompute won't re-add it
      const isVi = !country || country.toUpperCase() === 'VI';
      const fieldName = isVi ? `extractWords${level}LevelUS` : `extractWords${level}Level`;

      const currentWordsFromArticle = normalizeWords(
        (article as unknown as Record<string, unknown> | null)?.[fieldName]
      ).map(w => w.toLowerCase());

      const nextWords = currentWordsFromArticle.filter(w => w !== normalizedWord);

      await retryAsync(() =>
        articleAdminApi.update(country || '', articleId, {
          [vocabFieldName]: nextVocab,
          [fieldName]: nextWords,
        })
      );

      setDictionaryByLevel(prev => ({
        ...prev,
        [level]: (prev[level] || []).filter(
          entry => entry.word.trim().toLowerCase() !== normalizedWord
        ),
      }));

      setArticle(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          [vocabFieldName]: nextVocab,
          [fieldName]: nextWords,
        } as Article;
      });

      showNotification('Dictionary entry deleted', 'success');
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to delete entry', 'error');
    } finally {
      setDeletingEntries(prev => {
        const next = new Set(prev);
        next.delete(normalizedWord);
        return next;
      });
    }
  };

  // Keep a stable min for the datetime-local input so it doesn't move while typing
  const initialMinRef = React.useRef<string>(nowLocalDatetimeInput());
  // Keep a local input string so users can type freely without the controlled value
  // being reformatted mid-edit. Sync from article when it loads/changes.
  const [publishedAtInput, setPublishedAtInput] = useState<string>('');

  useEffect(() => {
    setPublishedAtInput(toLocalDatetimeInput(article?.publishedAt));
  }, [article?.publishedAt]);

  const handlePublish = () => {
    if (!article || !article.publishedAt) {
      setPublishedAtError('Please choose a publish date and time');
      return;
    }
    const t = new Date(article.publishedAt);
    if (Number.isNaN(t.getTime())) {
      setPublishedAtError('Please choose a valid publish date and time');
      return;
    }
    const now = new Date();
    if (t < now) {
      setPublishedAtError('Publish date must be today or in the future');
      return;
    }
    setPublishedAtError(null);
    setShowPublishConfirmation(true);
  };

  const levelBExtractWords = getExtractWordsByLevel(article, 'B');
  const levelCExtractWords = getExtractWordsByLevel(article, 'C');

  const handlePublishNow = () => {
    if (!article) return;
    const now = new Date();
    const nowIso = now.toISOString();
    setPublishedAtInput(toLocalDatetimeInput(nowIso));
    setArticle(prev => (prev ? { ...prev, publishedAt: nowIso } : prev));
    setPublishedAtError(null);
    setShowPublishConfirmation(true);
  };

  const performPublish = async () => {
    if (!article) return;
    // Close the confirm modal right away so the UI isn't blocked by it
    setShowPublishConfirmation(false);
    setIsLoading(true);
    try {
      await articleAdminApi.publishArticle(
        country!,
        articleId!,
        String(article.category || ''),
        String(article.publishedAt || '')
      );
      navigate('/editor/articles', {
        state: {
          notification: { message: 'Article published', type: 'success' },
          activeTab: 'publish',
        },
      });
    } catch (err) {
      showNotification('Failed to publish article', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {isLoading && <LoadingOverlay text="Publishing article..." />}
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <ConfirmModal
        open={showPublishConfirm}
        title="Confirm Publish Article"
        description="Are you sure you want to publish this article?"
        confirmText="Yes, publish"
        cancelText="Cancel"
        isLoading={isLoading}
        onConfirm={performPublish}
        onCancel={() => setShowPublishConfirmation(false)}
      />
      <ConfirmModal
        open={Boolean(deleteConfirm)}
        title="Confirm Delete Dictionary Entry"
        description={
          deleteConfirm
            ? `Are you sure you want to delete "${deleteConfirm.word}"? This cannot be undone.`
            : 'Are you sure you want to delete this dictionary entry?'
        }
        confirmText="Yes, delete"
        cancelText="Cancel"
        isLoading={Boolean(
          deleteConfirm &&
          deletingEntries.has(
            String(deleteConfirm.word || '')
              .trim()
              .toLowerCase()
          )
        )}
        onConfirm={async () => {
          if (!deleteConfirm) return;
          const { level, word } = deleteConfirm;
          await handleDeleteEntry(level, word);
          setDeleteConfirm(null);
        }}
        onCancel={() => setDeleteConfirm(null)}
      />
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Publish Article</h1>
          <p className="text-gray-600 mt-2">Choose a date to publish the article</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/editor/articles', { state: { activeTab: 'publish' } })}
        >
          ← Back to Articles
        </Button>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <span className="text-sm font-medium text-gray-700">Status:</span>
              {(originalStatus !== null ? originalStatus : isPublished(article)) === 1 && (
                <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                  Published
                </span>
              )}
              {(originalStatus !== null ? originalStatus : isPublished(article)) === 0 && (
                <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                  Scheduled to be Published
                </span>
              )}
              {(originalStatus !== null ? originalStatus : isPublished(article)) === -1 && (
                <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800">
                  Not Published
                </span>
              )}
            </div>

            <div>
              <div className="flex items-center gap-3">
                <label htmlFor="publishedAt" className="text-sm font-medium text-gray-700">
                  Published at:{' '}
                </label>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePublishNow}
                  disabled={isLoading || !article}
                  className="h-8 px-3 py-1 text-xs"
                >
                  Publish Now
                </Button>
              </div>

              <div className="mt-2 flex items-center gap-4">
                <input
                  id="publishedAt"
                  name="publishedAt"
                  type="datetime-local"
                  value={publishedAtInput}
                  min={initialMinRef.current}
                  onChange={e => {
                    setPublishedAtError(null);
                    setPublishedAtInput(e.target.value);
                  }}
                  onBlur={() => {
                    // Only commit the value to the article state when the user leaves the field
                    if (!publishedAtInput) {
                      setArticle(prev => (prev ? { ...prev, publishedAt: '' } : prev));
                      return;
                    }
                    const iso = fromLocalDatetimeInput(publishedAtInput);
                    const d = new Date(iso);
                    if (Number.isNaN(d.getTime())) {
                      setPublishedAtError('Please choose a valid publish date and time');
                      return;
                    }
                    const now = new Date();
                    if (d < now) {
                      setPublishedAtError('Publish date must be today or in the future');
                      return;
                    }
                    setPublishedAtError(null);
                    setArticle(prev => (prev ? { ...prev, publishedAt: iso } : prev));
                  }}
                  className="border rounded px-2 py-1 text-sm"
                />

                <Button
                  type="button"
                  onClick={handlePublish}
                  disabled={isLoading || Boolean(publishedAtError) || !article?.publishedAt}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Publish
                </Button>
              </div>

              {publishedAtError && <p className="text-sm text-red-600 mt-1">{publishedAtError}</p>}
            </div>

            <div className="text-sm text-gray-700">
              <span className="font-medium">Category:</span>{' '}
              {categories.find(c => c.categoryCode === article?.category)?.categoryNameEN ||
                'No category'}
            </div>
            <div>
              <span className="font-medium text-sm text-gray-700">Created at:</span>{' '}
              <span className="text-sm text-gray-700">
                {article ? new Date(article?.createdAt).toLocaleString() : ''}
              </span>
            </div>

            <div>
              <span className="font-medium text-sm text-gray-700">Title:</span>{' '}
              <span className="text-sm text-gray-700">{article?.title || article?.titleUS}</span>
            </div>

            <div>
              <span className="font-medium text-sm text-gray-700">Slug:</span>{' '}
              <span className="text-sm text-gray-700">{article?.slug || article?.slugUS}</span>
            </div>
          </div>

          {article?.articleImageSignedUrl && (
            <div className="mb-6">
              <img
                src={article.articleImageSignedUrl}
                alt={article?.title || article?.titleUS || 'Article'}
                className="w-full max-h-96 object-contain rounded-lg shadow-md"
                loading="eager"
                decoding="async"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          <div>
            <div>
              <label htmlFor="textALevel" className="block text-sm font-medium text-gray-700 mb-1">
                Content level A ({getLevelLabel('A')})
              </label>
              {(article?.ttsALevelENSignedUrl || article?.ttsALevelUSSignedUrl) && (
                <div className="mb-2">
                  <audio
                    controls
                    src={article?.ttsALevelENSignedUrl || article?.ttsALevelUSSignedUrl}
                    className="w-full"
                  />
                </div>
              )}
              {(article?.textALevelEN || article?.textALevelUS) && (
                <div className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                  {article?.textALevelEN || article?.textALevelUS}
                </div>
              )}
              {article?.textALevelVI && (
                <div className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                  {article.textALevelVI}
                </div>
              )}
              {article?.keyTakeawayALevelVI && (
                <div>
                  <p className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                    Key Takeaways - Level A
                  </p>
                  <p className="text-gray-700 bg-gray-50 rounded-md px-3 py-2 text-sm">
                    {article.keyTakeawayALevelVI}
                  </p>
                </div>
              )}
              {renderDictionaryByLevel('A')}
            </div>

            <div>
              <label htmlFor="textBLevel" className="block text-sm font-medium text-gray-700 mb-1">
                Content level B ({getLevelLabel('B')})
              </label>
              {(article?.ttsBLevelENSignedUrl || article?.ttsBLevelUSSignedUrl) && (
                <div className="mb-2">
                  <audio
                    controls
                    src={article?.ttsBLevelENSignedUrl || article?.ttsBLevelUSSignedUrl}
                    className="w-full"
                  />
                </div>
              )}
              {(article?.textBLevelEN || article?.textBLevelUS) && (
                <div className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                  {article?.textBLevelEN || article?.textBLevelUS}
                </div>
              )}
              {article?.textBLevelVI && (
                <div className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                  {article.textBLevelVI}
                </div>
              )}
              {article?.keyTakeawayBLevelVI && (
                <div>
                  <p className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                    Key Takeaways - Level B
                  </p>
                  <p className="text-gray-700 bg-gray-50 rounded-md px-3 py-2 text-sm">
                    {article.keyTakeawayBLevelVI}
                  </p>
                </div>
              )}
              {levelBExtractWords.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {levelBExtractWords.map(w => (
                    <span
                      key={w || 'empty'}
                      className="px-2 py-1 bg-green-50 text-green-800 text-xs rounded"
                    >
                      {w}
                    </span>
                  ))}
                </div>
              )}
              {renderDictionaryByLevel('B')}
            </div>

            <div>
              <label htmlFor="textCLevel" className="block text-sm font-medium text-gray-700 mb-1">
                Content level C ({getLevelLabel('C')})
              </label>
              {(article?.ttsCLevelENSignedUrl || article?.ttsCLevelUSSignedUrl) && (
                <div className="mb-2">
                  <audio
                    controls
                    src={article?.ttsCLevelENSignedUrl || article?.ttsCLevelUSSignedUrl}
                    className="w-full"
                  />
                </div>
              )}
              {(article?.textCLevelEN || article?.textCLevelUS) && (
                <div className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                  {article?.textCLevelEN || article?.textCLevelUS}
                </div>
              )}
              {article?.textCLevelVI && (
                <div className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                  {article.textCLevelVI}
                </div>
              )}
              {article?.keyTakeawayCLevelVI && (
                <div>
                  <p className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap rounded">
                    Key Takeaways - Level C
                  </p>
                  <p className="text-gray-700 bg-gray-50 rounded-md px-3 py-2 text-sm">
                    {article.keyTakeawayCLevelVI}
                  </p>
                </div>
              )}
              {levelCExtractWords.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {levelCExtractWords.map(w => (
                    <span
                      key={w || 'empty'}
                      className="px-2 py-1 bg-orange-50 text-orange-800 text-xs rounded"
                    >
                      {w}
                    </span>
                  ))}
                </div>
              )}
              {renderDictionaryByLevel('C')}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
